/**
 * Client-side helpers for the per-workspace "Trusted senders" admin
 * settings card on the Brand page.
 *
 * Background: an earlier iteration of MailCraft drew a "Verified
 * sender" badge from a hand-curated client allow-list. That has since
 * been replaced by real cryptographic DMARC/BIMI verification at
 * ingest (see `artifacts/api-server/src/lib/senderVerification.ts`),
 * so the client no longer needs an `isTrustedSender` matcher.
 *
 * What's kept here is the small input validator the admin form uses
 * before POSTing a new domain to `POST /api/trusted-senders` — it
 * mirrors the server's normaliser so we can reject obviously bad
 * input inline without a round trip.
 */

const DOMAIN_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Loose validator + normaliser for an admin-supplied trusted-sender
 * domain. Returns the canonical form (trimmed + lower-cased) when the
 * input looks like a registrable hostname — labels of `[a-z0-9-]`
 * (no leading/trailing hyphen) separated by dots, two or more labels,
 * total length ≤ 253. Returns `null` for anything else.
 *
 * We intentionally do NOT consult the public-suffix list here — it
 * would balloon the bundle for marginal benefit. The server's
 * validator does the same shape check; this just keeps obviously bad
 * input (URLs, paths, emails, whitespace) out of the network round
 * trip.
 */
export function normalizeTrustedDomain(input: string | null | undefined): string | null {
  if (typeof input !== "string") return null;
  let value = input.trim().toLowerCase();
  if (!value) return null;

  // Tolerate a leading scheme or trailing path that an admin might
  // paste from a browser bar — strip them rather than rejecting.
  if (value.startsWith("http://")) value = value.slice("http://".length);
  if (value.startsWith("https://")) value = value.slice("https://".length);
  const slashIndex = value.indexOf("/");
  if (slashIndex >= 0) value = value.slice(0, slashIndex);
  // Strip a leading `*.` wildcard if the admin pastes one — the
  // server treats the saved root as covering all subdomains.
  if (value.startsWith("*.")) value = value.slice(2);
  // Strip a leading `www.` similarly so admins don't end up with two
  // entries that mean the same thing.
  if (value.startsWith("www.")) value = value.slice(4);

  if (value.length === 0 || value.length > 253) return null;
  if (value.includes("@") || value.includes(" ")) return null;

  const labels = value.split(".");
  if (labels.length < 2) return null;
  for (const label of labels) {
    if (!DOMAIN_LABEL_RE.test(label)) return null;
  }
  return value;
}
